import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from '../ui/Icon';
import { AudioFX } from '../ui/primitives';
import { usePackingStation } from '../contexts/PackingStationContext';
import { useKiosk } from './StationApp';

type Workstation = { id: number; station_id: string; name: string; location?: string; is_active?: boolean };

function StationSheet({ current, stations, onPick, onClose }: { current: string; stations: Workstation[]; onPick: (s: Workstation) => void; onClose: () => void }) {
  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h3>Zmień stację</h3>
        <p className="sub">Wybierz stanowisko robocze tego urządzenia</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {stations.map((s) => (
            <button key={s.id} className={`pkg-type ${s.station_id === current ? 'suggested' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 12 }} onClick={() => { AudioFX.click(); onPick(s); }}>
              <Icon name="cpu" size={22} style={{ color: 'var(--accent-text)' }} />
              <div><div className="pt-name" style={{ fontSize: 16 }}>{s.station_id}</div><div className="pt-dim">{s.name}{s.location ? ` · ${s.location}` : ''}</div></div>
            </button>
          ))}
          {stations.length === 0 && <div className="muted" style={{ fontSize: 14 }}>Brak aktywnych stacji — dodaj je w panelu admina.</div>}
        </div>
        <button className="big-btn bb-ghost" style={{ marginTop: 16 }} onClick={onClose}>Anuluj</button>
      </div>
    </div>
  );
}

export default function LoginScreen() {
  const navigate = useNavigate();
  const { login, apiUrl } = usePackingStation();
  const { push } = useKiosk();
  const [pin, setPin] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stationId, setStationId] = useState(() => localStorage.getItem('packingStationId') || '');
  const [stations, setStations] = useState<Workstation[]>([]);
  const [sheet, setSheet] = useState(false);

  // Tryb kiosk (aplikacja PackFlow Station): stacja jest STAŁA — sparowana kodem,
  // nie do wyboru. Nie pobieramy listy stanowisk (i nie pokazujemy błędu jej braku).
  const kiosk = typeof window !== 'undefined'
    && (localStorage.getItem('packflow.kiosk') === '1' || !!localStorage.getItem('packflow.station_token'));
  const kioskStationName = (typeof window !== 'undefined' && localStorage.getItem('packflow.station_name')) || '';
  const appVersion = (typeof window !== 'undefined' && localStorage.getItem('packflow.app_version'))
    || process.env.REACT_APP_VERSION || '';

  useEffect(() => {
    if (kiosk) return; // stacja stała z parowania — lista zbędna
    axios.get(`${apiUrl}/api/settings/workstations`)
      .then((r) => {
        const active = (r.data || []).filter((s: Workstation) => s.is_active !== false);
        setStations(active);
        if (!stationId && active.length === 1) setStationId(active[0].station_id);
        if (!stationId && active.length > 1) setSheet(true);
      })
      .catch(() => setErr('Nie można pobrać listy stanowisk'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  async function submit(fullPin: string) {
    if (!stationId) {
      setErr('Najpierw wybierz stację');
      setSheet(true);
      setPin('');
      return;
    }
    setBusy(true);
    try {
      localStorage.setItem('packingStationId', stationId);
      await login(fullPin, stationId);
      AudioFX.success();
      navigate('/packing-station/dashboard');
    } catch (e: any) {
      AudioFX.buzz();
      setErr(e?.message || 'Błędny PIN — spróbuj ponownie');
      setShake(true);
      setTimeout(() => { setShake(false); setPin(''); }, 500);
    } finally {
      setBusy(false);
    }
  }

  function press(d: string) {
    if (busy || pin.length >= 4) return;
    AudioFX.click();
    const next = pin + d;
    setPin(next);
    setErr(null);
    if (next.length === 4) setTimeout(() => submit(next), 160);
  }
  function del() {
    AudioFX.click();
    setPin((p) => p.slice(0, -1));
    setErr(null);
  }

  const current = stations.find((s) => s.station_id === stationId);
  const stationLabel = kiosk
    ? `${stationId || '—'}${kioskStationName ? ` · ${kioskStationName}` : ''}`
    : (current ? `${current.station_id} · ${current.location || current.name}` : stationId || 'Wybierz stację');

  return (
    <div className="login">
      <div className="logo"><Icon name="package-check" size={30} /></div>
      <div className="welcome">
        <h2>Zaloguj się PIN-em</h2>
        <p>Operator pakowni</p>
      </div>
      <div className="err">{err && <><Icon name="alert-circle" size={16} /> {err}</>}</div>
      <div className={`pin-dots ${shake ? 'shake' : ''}`}>
        {[0, 1, 2, 3].map((i) => <i key={i} className={i < pin.length ? 'on' : ''} />)}
      </div>
      <div className="keypad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} className="key" disabled={busy} onClick={() => press(String(n))}>{n}</button>
        ))}
        <button className="key blank" tabIndex={-1} />
        <button className="key" disabled={busy} onClick={() => press('0')}>0</button>
        <button className="key" disabled={busy} onClick={del}><Icon name="delete" size={26} /></button>
      </div>
      {kiosk ? (
        <div className="station-sel" style={{ cursor: 'default' }} title="Stanowisko przypisane przez parowanie">
          <Icon name="cpu" size={16} /> {stationLabel}
        </div>
      ) : (
        <button className="station-sel" onClick={() => setSheet(true)}>
          <Icon name="map-pin" size={16} /> {stationLabel}
        </button>
      )}
      <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
        {appVersion ? `v${appVersion} · ` : ''}developed by insolitum.ai
      </div>

      {sheet && !kiosk && (
        <StationSheet
          current={stationId}
          stations={stations}
          onPick={(s) => { setStationId(s.station_id); localStorage.setItem('packingStationId', s.station_id); setSheet(false); push(`Stacja: ${s.station_id}`, 'info'); }}
          onClose={() => setSheet(false)}
        />
      )}
    </div>
  );
}
